import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, DollarSign, Calendar, Download, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { supabase } from '@/lib/supabase/client';

interface MonthlyRevenue {
  month: string;
  gross_bookings: number;
  discounts: number;
  gross_revenue: number;
  payment_costs: number;
  platform_fees: number;
  net_revenue: number;
  booking_count: number;
  attendee_count: number;
}

interface ExperienceRevenue {
  experience_id: string;
  title: string;
  gross_bookings: number;
  discounts: number;
  gross_revenue: number;
  payment_costs: number;
  net_revenue: number;
  booking_count: number;
  attendee_count: number;
  avg_booking_value: number;
  net_rev_per_attendee: number;
}

export function CreatorRevenueAnalytics() {
  const { user, profile } = useUnifiedAuth();
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenue[]>([]);
  const [experienceRevenue, setExperienceRevenue] = useState<ExperienceRevenue[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'30d' | '90d' | '12m' | 'all'>('12m');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchRevenueData();
    }
  }, [user, selectedPeriod]);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Get creator profile
      const { data: creatorProfile, error: profileError } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !creatorProfile) {
        throw new Error('Creator profile not found');
      }

      // Get date range based on selected period
      const now = new Date();
      let startDate: Date;
      switch (selectedPeriod) {
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case '12m':
          startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
          break;
        default:
          startDate = new Date(2020, 0, 1); // All time
      }

      // Get all bookings with experience details
      const { data: bookings, error: bookingError } = await supabase
        .from('bookings')
        .select(`
          id,
          experience_id,
          total_amount,
          discount_amount,
          participant_count,
          created_at,
          experience:experiences!inner(id, title, creator_profile_id)
        `)
        .eq('experience.creator_profile_id', creatorProfile.id)
        .eq('payment_status', 'paid')
        .eq('status', 'confirmed')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (bookingError) throw bookingError;

      // Process monthly revenue
      const monthlyMap = new Map<string, MonthlyRevenue>();
      const experienceMap = new Map<string, ExperienceRevenue>();

      bookings?.forEach(booking => {
        const bookingDate = new Date(booking.created_at);
        const monthKey = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}`;
        
        // Calculate fees
        const grossAmount = booking.total_amount || 0; // What was actually paid
        const discountAmount = booking.discount_amount || 0;
        const stripeFee = grossAmount * 0.045; // 4.5% estimate on actual payment amount
        const platformFee = 0; // Not collecting platform fees currently
        const netAmount = grossAmount - stripeFee - platformFee;

        // Update monthly revenue
        if (!monthlyMap.has(monthKey)) {
          monthlyMap.set(monthKey, {
            month: monthKey,
            gross_bookings: 0,
            discounts: 0,
            gross_revenue: 0,
            payment_costs: 0,
            platform_fees: 0,
            net_revenue: 0,
            booking_count: 0,
            attendee_count: 0
          });
        }

        const monthData = monthlyMap.get(monthKey)!;
        monthData.gross_bookings += grossAmount; // Sum of actual paid amounts
        monthData.discounts += discountAmount;
        monthData.gross_revenue += (grossAmount - discountAmount); // Gross bookings less discounts
        monthData.payment_costs += stripeFee;
        monthData.platform_fees += platformFee;
        monthData.net_revenue += netAmount;
        monthData.booking_count += 1;
        monthData.attendee_count += booking.participant_count || 0;

        // Update experience revenue
        const exp = booking.experience as any;
        if (!experienceMap.has(booking.experience_id)) {
          experienceMap.set(booking.experience_id, {
            experience_id: booking.experience_id,
            title: exp?.title || 'Unknown',
            gross_bookings: 0,
            discounts: 0,
            gross_revenue: 0,
            payment_costs: 0,
            net_revenue: 0,
            booking_count: 0,
            attendee_count: 0,
            avg_booking_value: 0,
            net_rev_per_attendee: 0
          });
        }

        const expData = experienceMap.get(booking.experience_id)!;
        expData.gross_bookings += grossAmount; // Sum of actual paid amounts
        expData.discounts += discountAmount;
        expData.gross_revenue += (grossAmount - discountAmount); // Gross bookings less discounts
        expData.payment_costs += stripeFee;
        expData.net_revenue += netAmount;
        expData.booking_count += 1;
        expData.attendee_count += booking.participant_count || 0;
      });

      // Calculate averages for experiences
      experienceMap.forEach(exp => {
        exp.avg_booking_value = exp.booking_count > 0 ? exp.gross_revenue / exp.booking_count : 0;
        exp.net_rev_per_attendee = exp.attendee_count > 0 ? exp.net_revenue / exp.attendee_count : 0;
      });

      setMonthlyRevenue(Array.from(monthlyMap.values()).sort((a, b) => a.month.localeCompare(b.month)));
      setExperienceRevenue(Array.from(experienceMap.values()).sort((a, b) => b.net_revenue - a.net_revenue));
    } catch (err) {
      console.error('Failed to fetch revenue data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const exportRevenueCSV = () => {
    const headers = ['Month', 'Gross Bookings', 'Discounts', 'Gross Revenue', 'Payment Processing', 'Net Revenue', 'Bookings', 'Attendees'];
    const rows = monthlyRevenue.map(month => [
      month.month,
      month.gross_bookings.toFixed(2),
      month.discounts.toFixed(2),
      month.gross_revenue.toFixed(2),
      month.payment_costs.toFixed(2),
      month.net_revenue.toFixed(2),
      month.booking_count,
      month.attendee_count
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-sand-400">Loading revenue analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-500">{error}</p>
        <Button variant="outline" size="sm" onClick={fetchRevenueData} className="mt-2">
          Retry
        </Button>
      </div>
    );
  }

  const totalRevenue = monthlyRevenue.reduce((sum, month) => sum + month.net_revenue, 0);
  const totalBookings = monthlyRevenue.reduce((sum, month) => sum + month.booking_count, 0);
  const totalAttendees = monthlyRevenue.reduce((sum, month) => sum + month.attendee_count, 0);

  return (
    <div className="space-y-6">
      {/* Header & Period Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-display text-sand-100">Revenue Analytics</h2>
          <p className="text-sand-400 text-sm">Detailed breakdown following accounting standards</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-earth-800/50 p-1 rounded-lg">
            {(['30d', '90d', '12m', 'all'] as const).map(period => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  selectedPeriod === period
                    ? 'bg-sand-400 text-earth-900'
                    : 'text-sand-400 hover:text-sand-100'
                }`}
              >
                {period === '30d' && '30 Days'}
                {period === '90d' && '90 Days'}
                {period === '12m' && '12 Months'}
                {period === 'all' && 'All Time'}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={exportRevenueCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-earth-800/50 p-6 rounded-lg border border-earth-700/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-sand-400">Net Revenue</span>
            <TrendingUp className="h-4 w-4 text-green-400" />
          </div>
          <p className="text-2xl font-bold text-sand-100">{formatCurrency(totalRevenue)}</p>
          <p className="text-sm text-sand-400 mt-1">After all fees</p>
        </div>
        
        <div className="bg-earth-800/50 p-6 rounded-lg border border-earth-700/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-sand-400">Total Bookings</span>
            <Calendar className="h-4 w-4 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-sand-100">{totalBookings}</p>
          <p className="text-sm text-sand-400 mt-1">Confirmed & paid</p>
        </div>
        
        <div className="bg-earth-800/50 p-6 rounded-lg border border-earth-700/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-sand-400">Net per Attendee</span>
            <DollarSign className="h-4 w-4 text-purple-400" />
          </div>
          <p className="text-2xl font-bold text-sand-100">
            {formatCurrency(totalAttendees > 0 ? totalRevenue / totalAttendees : 0)}
          </p>
          <p className="text-sm text-sand-400 mt-1">{totalAttendees} total attendees</p>
        </div>
      </div>

      {/* Monthly Revenue Table */}
      <div className="bg-earth-800/50 rounded-lg border border-earth-700/50 overflow-hidden">
        <div className="p-4 border-b border-earth-700/50">
          <h3 className="text-lg font-display text-sand-100">Monthly Revenue Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-earth-800/80">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Month</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Gross Bookings</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Discounts</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Gross Revenue</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Payment Fees (Est.)</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Net Revenue</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Margin</th>
              </tr>
            </thead>
            <tbody>
              {monthlyRevenue.map((month, index) => {
                const margin = month.gross_revenue > 0 ? month.net_revenue / month.gross_revenue : 0;
                return (
                  <motion.tr
                    key={month.month}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-t border-earth-700/50 hover:bg-earth-700/20"
                  >
                    <td className="p-4">
                      <p className="text-sand-100">{month.month}</p>
                      <p className="text-xs text-sand-400">
                        {month.booking_count} bookings • {month.attendee_count} attendees
                      </p>
                    </td>
                    <td className="p-4 text-right font-mono text-sand-100">
                      {formatCurrency(month.gross_bookings)}
                    </td>
                    <td className="p-4 text-right font-mono text-sand-400">
                      {month.discounts > 0 ? `-${formatCurrency(month.discounts)}` : formatCurrency(0)}
                    </td>
                    <td className="p-4 text-right font-mono text-sand-100">
                      {formatCurrency(month.gross_revenue)}
                    </td>
                    <td className="p-4 text-right font-mono text-red-400">
                      -{formatCurrency(month.payment_costs)}
                    </td>
                    <td className="p-4 text-right font-mono text-green-400 font-bold">
                      {formatCurrency(month.net_revenue)}
                    </td>
                    <td className="p-4 text-right text-sand-400">
                      {formatPercent(margin)}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Experience Performance */}
      <div className="bg-earth-800/50 rounded-lg border border-earth-700/50 overflow-hidden">
        <div className="p-4 border-b border-earth-700/50">
          <h3 className="text-lg font-display text-sand-100">Revenue by Experience</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-earth-800/80">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Experience</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Bookings</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Attendees</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Avg Booking</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Net Revenue</th>
                <th className="text-right p-4 text-sm font-medium text-sand-300">Per Attendee</th>
              </tr>
            </thead>
            <tbody>
              {experienceRevenue.map((exp, index) => (
                <motion.tr
                  key={exp.experience_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-t border-earth-700/50 hover:bg-earth-700/20"
                >
                  <td className="p-4">
                    <p className="text-sand-100 font-medium">{exp.title}</p>
                  </td>
                  <td className="p-4 text-right text-sand-100">
                    {exp.booking_count}
                  </td>
                  <td className="p-4 text-right text-sand-100">
                    {exp.attendee_count}
                  </td>
                  <td className="p-4 text-right font-mono text-sand-100">
                    {formatCurrency(exp.avg_booking_value)}
                  </td>
                  <td className="p-4 text-right font-mono text-green-400 font-bold">
                    {formatCurrency(exp.net_revenue)}
                  </td>
                  <td className="p-4 text-right font-mono text-sand-400">
                    {formatCurrency(exp.net_rev_per_attendee)}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Platform Fee Notice */}
      <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
        <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-500">
          <p className="font-medium mb-1">Fee Structure</p>
          <p>Payment processing: 4.5% (estimate - actual fees coming soon)</p>
          <p>Platform fee: Currently not collected</p>
        </div>
      </div>
    </div>
  );
}
